import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import globalAxios from "axios";
import { FiAlertTriangle, FiCheckCircle, FiFile, FiSearch, FiTool, FiTrash2 } from "react-icons/fi";
import { Modal } from "../../Components/Modal";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import GatedButton from "../../Components/GatedButton";
import { ChangeDiffViewer } from "../../Components/diff/ChangeDiffViewer";
import { useDeploymentDiff } from "../../Components/Deployment/deploymentDiff";
import { useDeploymentRenderPair } from "../../Components/Deployment/DeploymentVisualPair";
import {
	OutcomeChip,
	kindLabelFr,
	normalizeJobStatus,
	normalizeReport,
	type PreviewRepairIssue,
	type PreviewRepairReport,
	type RepairJobStatus
} from "./PreviewRepairSection";

/**
 * « Analyser les fichiers déployés » : la revue de ce qui est DÉJÀ en ligne. Le serveur compare
 * chaque fichier final de la cible (preview ou production) à ce que la source produirait
 * aujourd'hui, liste chaque écart (fichier différent, manquant, orphelin sans source, marqueur)
 * et, pour l'entrée sélectionnée, montre la différence texte entre le fichier servi et le
 * fichier attendu. « Corriger tout » réécrit uniquement les écarts prouvés, via le pipeline
 * normal de publication, avec une barre de progression (éléments restants). Sur la production,
 * un contenu qui attend une publication éditoriale est signalé « en file » et jamais touché :
 * la file de publication (page Déploiement) reste le seul chemin pour lui.
 */

type Target = "preview" | "production";

const FIXABLE_KINDS = new Set(["page-mismatch", "page-missing", "record-mismatch", "record-missing", "orphan-record", "orphan-file", "marker"]);

/** Fixable = would be rewritten/removed by « Corriger tout » (not queued, not a collision to solve by hand). */
export const isFixableIssue = (issue: PreviewRepairIssue): boolean =>
	issue.outcome !== "queued" && FIXABLE_KINDS.has(issue.kind) && !(issue.outcome ?? "").startsWith("fixed");

const KIND_ORDER = ["page-mismatch", "record-mismatch", "page-missing", "record-missing", "orphan-file", "orphan-record", "slug-collision", "template-duplicate", "marker"];

const kindTone = (kind: string): string => {
	if (kind.endsWith("mismatch")) return "border-amber-200 bg-amber-50 text-amber-700";
	if (kind.endsWith("missing")) return "border-sky-200 bg-sky-50 text-sky-700";
	if (kind.startsWith("orphan")) return "border-rose-200 bg-rose-50 text-rose-700";
	if (kind === "slug-collision") return "border-rose-200 bg-rose-50 text-rose-700";
	if (kind === "template-duplicate") return "border-amber-200 bg-amber-50 text-amber-700";
	return "border-slate-200 bg-slate-50 text-slate-600";
};

const KIND_HINT: Record<string, string> = {
	"page-mismatch": "Le fichier servi n'est pas celui que la source produirait : la correction le réécrit.",
	"record-mismatch": "La fiche servie n'est pas celle que la source produirait : la correction la réécrit.",
	"page-missing": "Attendu mais absent de la cible : la correction le publie.",
	"record-missing": "Fiche attendue mais absente de la cible : la correction la publie.",
	"orphan-file": "Fichier présent sur la cible sans page source : il ne devrait pas exister, la correction le supprime.",
	"orphan-record": "Ligne présente sur la cible sans fiche source : elle ne devrait pas exister, la correction la supprime.",
	"slug-collision": "Deux pages vivantes revendiquent le même chemin : à résoudre à la main (renommer ou supprimer le doublon).",
	"template-duplicate": "Plusieurs gabarits {path} publient le même type : la correction ne réécrit rien en bloc ; supprimez ou désactivez le gabarit en trop pour lever l'avertissement.",
	marker: "Contenu identique mais marqueur « déployé » absent : la correction pose le marqueur, rien n'est réécrit."
};

const targetLabel = (t: Target) => (t === "production" ? "production" : "preview");

/* ------------------------------------------------------------------ */
/*  Per-entry detail: text difference deployed vs expected              */
/* ------------------------------------------------------------------ */

const DiffPane: React.FC<{ before: unknown; after: unknown; beforeLabel: string; afterLabel: string }> = ({ before, after, beforeLabel, afterLabel }) => (
	<ChangeDiffViewer before={before ?? {}} after={after ?? {}} initialMode="unified" beforeLabel={beforeLabel} afterLabel={afterLabel} />
);

/** Detail of a record issue: the projected pair (dry run vs served row) when the record has a page, else its raw artefact diff. */
const RecordIssueDetail: React.FC<{ issue: PreviewRepairIssue; target: Target }> = ({ issue, target }) => {
	const pair = useDeploymentRenderPair(issue.contentId, target, true);
	const needsRawDiff = !pair.loading && !pair.error && pair.pair != null && !pair.pair.supported;
	const raw = useDeploymentDiff(issue.contentId, target, needsRawDiff);
	const served = `Servi (${targetLabel(target)})`;
	if (pair.loading) return <p className="text-sm text-slate-500">Comparaison en cours…</p>;
	if (pair.error) return <p className="text-sm text-rose-600">{pair.error}</p>;
	if (pair.pair?.supported) {
		if (!pair.pair.deployed && !pair.pair.expected) return <p className="text-sm text-slate-500">Rien à comparer.</p>;
		return <DiffPane before={pair.pair.deployed} after={pair.pair.expected} beforeLabel={served} afterLabel="Attendu (source)" />;
	}
	if (raw.loading) return <p className="text-sm text-slate-500">Comparaison en cours…</p>;
	if (raw.error) return <p className="text-sm text-rose-600">{raw.error}</p>;
	if (raw.diff && raw.diff.supported) {
		if (raw.diff.identical) return <p className="text-sm text-emerald-700">Le fichier servi est identique à la source (l'écart signalé portait sur le ledger ou le marqueur).</p>;
		return <DiffPane before={raw.diff.deployed} after={raw.diff.expected} beforeLabel={served} afterLabel="Attendu (source)" />;
	}
	return <p className="text-sm text-slate-500">{pair.pair?.reason ?? raw.diff?.reason ?? "Aucune comparaison disponible pour cette entrée."}</p>;
};

/** Detail of a page issue: the deployed page.json against the page the source produces. */
const PageIssueDetail: React.FC<{ issue: PreviewRepairIssue; target: Target }> = ({ issue, target }) => {
	const raw = useDeploymentDiff(issue.contentId, target, true);
	if (raw.loading) return <p className="text-sm text-slate-500">Comparaison en cours…</p>;
	if (raw.error) return <p className="text-sm text-rose-600">{raw.error}</p>;
	if (!raw.diff) return null;
	if (!raw.diff.supported) return <p className="text-sm text-slate-500">{raw.diff.reason}</p>;
	if (raw.diff.identical) return <p className="text-sm text-emerald-700">Le fichier servi est identique à la source (l'écart signalé portait sur le ledger ou le marqueur).</p>;
	return <DiffPane before={raw.diff.deployed} after={raw.diff.expected} beforeLabel={`Servi (${targetLabel(target)})`} afterLabel="Attendu (source)" />;
};

export const IssueDetail: React.FC<{ issue: PreviewRepairIssue; target: Target }> = ({ issue, target }) => {
	const isRecord = issue.kind.startsWith("record") || issue.kind === "orphan-record";
	const isPage = issue.kind.startsWith("page") || issue.kind === "marker" || issue.kind === "slug-collision";
	return (
		<div className="space-y-3" data-testid="issue-detail">
			<div className="flex flex-wrap items-center gap-2">
				<span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${kindTone(issue.kind)}`}>{kindLabelFr(issue.kind)}</span>
				<span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-800" title={issue.location ?? issue.contentId ?? ""}>
					{issue.location ?? issue.contentId ?? "?"}
					{issue.language && <span className="ml-1.5 font-sans text-[11px] text-slate-400">({issue.language})</span>}
					{issue.schemaKey && <span className="ml-1.5 font-sans text-[11px] text-slate-400">{issue.schemaKey}</span>}
				</span>
				<OutcomeChip outcome={issue.outcome} />
			</div>
			<p className="text-xs text-slate-600">{issue.detail}</p>
			<p className="text-xs text-slate-500">{KIND_HINT[issue.kind] ?? ""}</p>
			{issue.outcome === "queued" && (
				<p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
					Ce contenu attend une publication éditoriale dans la file de production : la correction ne le touche pas. Publiez-le depuis la file pour le mettre en ligne.
				</p>
			)}
			{issue.kind === "orphan-file" && (
				<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
					<FiTrash2 className="mr-1 inline" /> Fichier déployé sans page source : {issue.location}. Aucun contenu admin ne le produit ; il sera retiré de la cible.
				</p>
			)}
			{issue.contentId && isRecord && issue.kind !== "orphan-record" && <RecordIssueDetail issue={issue} target={target} />}
			{issue.contentId && isPage && <PageIssueDetail issue={issue} target={target} />}
			{issue.kind === "orphan-record" && (
				<p className="text-xs text-slate-500">Ligne servie à {issue.location} sans fiche source : rien à comparer, elle sera supprimée.</p>
			)}
		</div>
	);
};

/* ------------------------------------------------------------------ */
/*  Modal                                                               */
/* ------------------------------------------------------------------ */

const issueKey = (issue: PreviewRepairIssue, i: number) => `${issue.kind}|${issue.location ?? ""}|${issue.contentId ?? ""}|${i}`;

export const IntegrityAnalyzeModal: React.FC<{ show: boolean; onHide: () => void; pollMs?: number }> = ({ show, onHide, pollMs = 2000 }) => {
	const [target, setTarget] = useState<Target>("production");
	const [status, setStatus] = useState<RepairJobStatus | null>(null);
	const [report, setReport] = useState<PreviewRepairReport | null>(null);
	const [running, setRunning] = useState(false);
	const [starting, setStarting] = useState(false);
	const [phase, setPhase] = useState<"scan" | "fix" | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [selectedKey, setSelectedKey] = useState<string | null>(null);
	const [confirmFix, setConfirmFix] = useState(false);
	const [ack, setAck] = useState(false);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const mountedRef = useRef(true);

	const stopPolling = useCallback(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const fetchStatus = useCallback(async (): Promise<boolean> => {
		try {
			const res = await globalAxios.get(`/api/deployment/preview/repair/status`, { withCredentials: true, headers: { Accept: "application/json" } });
			if (!mountedRef.current) return false;
			const d = res.data ?? {};
			const next = normalizeJobStatus(d.status);
			const isRunning = d.running === true || next.state === "running";
			setStatus(next);
			setReport(normalizeReport(d.report));
			setRunning(isRunning);
			return isRunning;
		} catch (err) {
			console.error(err);
			return true;
		}
	}, []);

	const startPolling = useCallback(() => {
		stopPolling();
		timerRef.current = setInterval(async () => {
			const still = await fetchStatus();
			if (!still) stopPolling();
		}, pollMs);
	}, [fetchStatus, pollMs, stopPolling]);

	useEffect(() => {
		mountedRef.current = true;
		if (!show) return;
		(async () => {
			const isRunning = await fetchStatus();
			if (isRunning && mountedRef.current) startPolling();
		})();
		return () => {
			mountedRef.current = false;
			stopPolling();
		};
	}, [show, fetchStatus, startPolling, stopPolling]);

	const start = useCallback(
		async (which: "scan" | "fix") => {
			setStarting(true);
			setError(null);
			setSelectedKey(null);
			try {
				const url = which === "scan" ? `/api/deployment/${target}/integrity-scan` : `/api/deployment/${target}/realign`;
				await globalAxios.post(url, null, {
					withCredentials: true,
					headers: {
						Accept: "application/json",
						...(which === "fix" && target === "production" ? { "X-Confirm-Destructive": "I understand" } : {})
					}
				});
				setPhase(which);
				setRunning(true);
				await fetchStatus();
				startPolling();
			} catch (err: any) {
				console.error(err);
				if (err?.response?.status === 409) setError(String(err.response?.data?.error ?? "Une opération de maintenance est déjà en cours."));
				else setError(which === "scan" ? "Impossible de lancer l'analyse." : "Impossible de lancer la correction.");
			} finally {
				setStarting(false);
			}
		},
		[fetchStatus, startPolling, target]
	);

	const issues = report?.issues ?? [];
	const groups = useMemo(() => {
		const byKind = new Map<string, Array<{ issue: PreviewRepairIssue; key: string }>>();
		issues.forEach((issue, i) => {
			const key = issueKey(issue, i);
			const bucket = byKind.get(issue.kind) ?? [];
			bucket.push({ issue, key });
			byKind.set(issue.kind, bucket);
		});
		return Array.from(byKind.entries()).sort((a, b) => {
			const ia = KIND_ORDER.indexOf(a[0]);
			const ib = KIND_ORDER.indexOf(b[0]);
			return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
		});
	}, [issues]);
	const selected = useMemo(() => {
		if (!selectedKey) return null;
		for (const [, list] of groups) for (const entry of list) if (entry.key === selectedKey) return entry.issue;
		return null;
	}, [groups, selectedKey]);

	const fixable = issues.filter(isFixableIssue).length;
	const queued = issues.filter((i) => i.outcome === "queued").length;
	const orphans = issues.filter((i) => i.kind.startsWith("orphan")).length;
	const warnings = issues.filter((i) => i.kind === "template-duplicate").length;
	const gaps = issues.length - warnings;
	const jobFailed = !running && status?.state === "failed";
	const showReport = !running && report != null;
	const remaining = status ? Math.max(0, status.total - status.done) : 0;
	const pct = status && status.total > 0 ? Math.min(100, Math.round((status.done / status.total) * 100)) : 0;
	const recordsChecked = (report?.customRecordsChecked ?? 0) + (report?.dynamicRecordsChecked ?? 0);
	// A resumed job (opened while another admin's job runs) reads its phase from the job name.
	const runningLabel =
		phase === "fix" || (phase == null && /repair/i.test(status?.target ?? ""))
			? "Correction en cours"
			: "Analyse en cours";

	return (
		<Modal show={show} onHide={running ? () => undefined : onHide} size="full85" centered>
			<Modal.Header closeButton={!running}>
				<Modal.Title>
					<span className="flex items-center gap-2">
						<FiFile /> Analyser les fichiers déployés
					</span>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="space-y-4">
				<div className="flex flex-wrap items-center gap-3">
					<div className="inline-flex overflow-hidden rounded-lg border border-slate-200 text-xs font-semibold" role="group" aria-label="Cible">
						{(["preview", "production"] as const).map((t) => (
							<button
								key={t}
								type="button"
								onClick={() => {
									setTarget(t);
									setSelectedKey(null);
								}}
								disabled={running || starting}
								aria-pressed={target === t}
								className={`px-3 py-1.5 transition ${target === t ? "bg-[#005CA9] text-white" : "bg-white text-slate-600 hover:bg-slate-50"} disabled:cursor-not-allowed disabled:opacity-60`}
							>
								{t === "preview" ? "Fichiers finaux preview" : "Fichiers finaux production"}
							</button>
						))}
					</div>
					<button
						type="button"
						onClick={() => start("scan")}
						disabled={running || starting}
						className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{starting && phase !== "fix" ? <LoadingSpinner size="sm" inline /> : <FiSearch className="h-3.5 w-3.5" />}
						Analyser la {targetLabel(target)}
					</button>
					{showReport && report && !report.applied && fixable > 0 && (
						<GatedButton
							token="feature:deployment:write"
							onClick={() => {
								setAck(false);
								setConfirmFix(true);
							}}
							disabled={running || starting}
							className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
								target === "production" ? "bg-rose-600 hover:bg-rose-700" : "bg-[#005CA9] hover:bg-[#004b8a]"
							}`}
						>
							<FiTool className="h-3.5 w-3.5" /> Corriger tout en {targetLabel(target)} ({fixable})
						</GatedButton>
					)}
					<p className="ml-auto max-w-xl text-[11px] text-slate-500">
						Compare les fichiers DÉJÀ publiés sur la cible à ce que la source produirait aujourd'hui. Les nouveaux
						changements en attente de publication sont dans la file de la page Déploiement, pas ici.
					</p>
				</div>

				{error && (
					<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
						{error}
					</p>
				)}
				{jobFailed && status?.error && (
					<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
						L'opération a échoué : {status.error}
					</p>
				)}

				{running && (
					<div className="rounded-xl border border-sky-200 bg-sky-50/60 px-4 py-3" data-testid="integrity-progress">
						<div className="flex flex-wrap items-center gap-2 text-sm font-medium text-sky-800">
							<LoadingSpinner size="sm" inline />
							{runningLabel}…
							{status && status.total > 0 && (
								<span className="font-normal text-sky-700">
									{status.done}/{status.total} · {remaining} restant{remaining > 1 ? "s" : ""}
								</span>
							)}
							{status && status.total === 0 && <span className="font-normal text-sky-700">préparation…</span>}
						</div>
						<div className="mt-2 h-2 overflow-hidden rounded-full bg-sky-100" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
							<div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${pct}%` }} />
						</div>
					</div>
				)}

				{showReport && report && (
					<>
						<div className={`rounded-xl border px-4 py-3 ${report.isClean ? "border-emerald-200 bg-emerald-50/70" : "border-amber-200 bg-amber-50/70"}`}>
							<p className={`flex items-center gap-2 text-sm font-semibold ${report.isClean ? "text-emerald-800" : "text-amber-800"}`}>
								{report.isClean ? <FiCheckCircle /> : <FiAlertTriangle />}
								{report.isClean
									? `Tous les fichiers de la ${targetLabel(target)} sont exactement ceux attendus.`
									: report.applied
										? `Correction terminée : ${report.redeploys} réécriture${report.redeploys > 1 ? "s" : ""}, ${report.orphansRemoved} orphelin${report.orphansRemoved > 1 ? "s" : ""} retiré${report.orphansRemoved > 1 ? "s" : ""}, ${report.markerFixes} marqueur${report.markerFixes > 1 ? "s" : ""}.`
										: gaps === 0
											? `Tous les fichiers de la ${targetLabel(target)} sont exactement ceux attendus ; ${warnings} avertissement${warnings > 1 ? "s" : ""} à traiter dans le contenu.`
											: `${gaps} écart${gaps > 1 ? "s" : ""} : ${fixable} corrigeable${fixable > 1 ? "s" : ""}${queued > 0 ? `, ${queued} en attente de publication (non touchés)` : ""}${orphans > 0 ? `, dont ${orphans} orphelin${orphans > 1 ? "s" : ""} qui ne devraient pas exister` : ""}${warnings > 0 ? `, ${warnings} avertissement${warnings > 1 ? "s" : ""}` : ""}.`}
							</p>
							<div className="mt-3 grid gap-2" style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))" }}>
								{[
									[report.pagesChecked, "Pages vérifiées"],
									[report.pagesIdentical, "Pages identiques"],
									[recordsChecked, "Fiches vérifiées"],
									[report.dynamicRecordsIdentical, "Fiches identiques"],
									[issues.length, "Écarts"],
									[`${(report.elapsedMs / 1000).toFixed(1)} s`, "Durée"]
								].map(([value, label]) => (
									<div key={String(label)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-center">
										<div className="text-sm font-bold text-slate-800">{value}</div>
										<div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
									</div>
								))}
							</div>
						</div>

						{issues.length > 0 && (
							<div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 420px) minmax(0, 1fr)", gap: "1rem", alignItems: "start" }}>
								<div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1" data-testid="integrity-issues">
									{groups.map(([kind, list]) => (
										<div key={kind} className="overflow-hidden rounded-xl border border-slate-200">
											<div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/80 px-3 py-2">
												<span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{kindLabelFr(kind)}</span>
												<span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 shadow-sm">{list.length}</span>
											</div>
											<ul className="divide-y divide-slate-100 bg-white">
												{list.map(({ issue, key }) => (
													<li key={key}>
														<button
															type="button"
															onClick={() => setSelectedKey(key)}
															className={`flex w-full items-start gap-2 px-3 py-2 text-left transition ${selectedKey === key ? "bg-[#005CA9]/5" : "hover:bg-slate-50"}`}
														>
															<div className="min-w-0 flex-1">
																<p className="truncate font-mono text-xs text-slate-800" title={issue.location ?? issue.contentId ?? ""}>
																	{issue.location ?? issue.contentId ?? "?"}
																</p>
																<p className="mt-0.5 truncate text-[11px] text-slate-500">{issue.detail}</p>
															</div>
															<OutcomeChip outcome={issue.outcome} />
														</button>
													</li>
												))}
											</ul>
										</div>
									))}
								</div>
								<div className="max-h-[60vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4">
									{selected ? (
										<IssueDetail issue={selected} target={target} />
									) : (
										<p className="py-10 text-center text-sm text-slate-500">Sélectionnez une entrée à gauche pour voir la différence texte entre le fichier servi et le fichier attendu.</p>
									)}
								</div>
							</div>
						)}
					</>
				)}

				{!running && !report && !error && !jobFailed && (
					<p className="py-8 text-center text-sm text-slate-500">Choisissez la cible puis lancez l'analyse : rien n'est écrit tant que vous ne corrigez pas.</p>
				)}
			</Modal.Body>
			<Modal.Footer>
				{confirmFix ? (
					<div className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
						<p className="min-w-0 flex-1 text-sm text-amber-900">
							{target === "production"
								? `La production sera réécrite pour ${fixable} entrée${fixable > 1 ? "s" : ""} (écarts prouvés uniquement, via le pipeline normal, jamais de purge). Les ${queued} contenu${queued > 1 ? "s" : ""} en attente de publication ne sont pas touchés.`
								: `La preview sera réécrite pour ${fixable} entrée${fixable > 1 ? "s" : ""} (écarts prouvés uniquement).`}
						</p>
						{target === "production" && (
							<label className="flex items-center gap-2 text-xs font-medium text-amber-900">
								<input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> Je comprends
							</label>
						)}
						<button type="button" onClick={() => setConfirmFix(false)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
							Annuler
						</button>
						<button
							type="button"
							disabled={target === "production" && !ack}
							onClick={() => {
								setConfirmFix(false);
								start("fix");
							}}
							className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
						>
							<FiTool className="h-3.5 w-3.5" /> Confirmer la correction
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={onHide}
						disabled={running}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{running ? "Opération en cours…" : "Fermer"}
					</button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default IntegrityAnalyzeModal;
